import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service'
import { of, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { environment as ENV } from '../../../environments/environment'
import { ApiResponse } from '../model/api.model'
import { catchAndHandleError, mapToMaybeData } from '../util'
import {
  LoanPortfolioSummary,
  SimulateMillimanResults,
  ModelRunStatusResponse,
  RunStatus,
  SimulateLeapsModelResponse,
  SimulateLeapsRequestPayload,
  SimulateMillimanRequestPayload,
} from '../../model-runner/model/model-runner.model'
import { convertAnimatedScenariosData } from '../../credit/utils/credit.utils'
import { CreditEmail } from '../../credit/model/credit-email.model'
import {
  BlobTransferResult,
  CreditModelScenarioMapping,
  CreditPortfolio,
  GsePortfolio,
} from '../../credit/model/credit-portfolio.model'
import {
  CreditStructure,
  GseStructure,
  CreditCalculationStructure,
  CreditStructureOptimizationInput,
  CreditAdminCalculationStructure,
  CallTypeSelector,
} from './../../credit/model/credit-structure.model'
import {
  CreditStructureGroup,
  CreditStructureGroupMember,
} from './../../credit/model/credit-structure-group.model'
import {
  GseStructureGroup,
  GseStructureGroupMember,
} from './../../credit/model/gse-structure-group.model'
import { CreditCompareMetricResponse } from './../../credit/model/credit-compare.model'
import { CreditMetricSetting } from './../../credit/model/credit-metric.model'
import { CreditResults, StructureResultGroup } from '../../credit/model/credit-results.model'
import {
  CashFlowsData,
  CreditGrossLossScenarioDatum,
} from '../../credit/model/credit-cash-flows.model'
import {
  AnimatedScenariosResults,
  AnimatedScenariosResultsData,
} from '../../credit/model/credit-animated-scenarios.model'
import {
  StratificationAxisField,
  StratificationFilters,
  StratificationResults,
} from '../../credit/model/credit-stratification.model'
import { CreditOptimizationResponse } from '../../credit/model/credit-optimization.model'
import { PerformanceField, PerformanceResults } from '../../credit/model/credit-performance.model'
import {
  CreditSubmissionAuthorizationRequest,
  CreditSubmissionReinsurer,
  CreditSubmissionStatusCombined,
  CreditSubmissionStatus,
  CreditSubmissionStructure,
  CreditSubmissionStructureWithProgramData,
  CreditSubmissionUserTranche,
} from '../../credit/model/credit-submission.model'
import {
  GseSubmissionReinsurer,
  GseSubmissionStatus,
  GseSubmissionStructure,
  GseSubmissionUserTranche,
} from '../../credit/model/gse-submission.model'
import {
  CreditFileType,
  CreditPortfolioUploadResults,
} from './../../admin/model/credit-admin.model'
import {
  CreditIssueFot,
  CreditQuoteAssignedLineRow,
  CreditQuoteAuditTable,
  CreditQuoteNewAssignedLine,
  CreditQuoteOverview,
  CreditQuoteSubmissionOverview,
  CreditTrancheQuoteList,
} from './../../credit/model/credit-quote.model'
import { ErrorComponent } from '../../error/error.component'
import { errorPayload } from '../../error/model/error'
import { BlobWithHeaders, CONTENT_DISPOSITION, ProblemDetails } from '../model/backend.model'

const API = ENV.internalApi

enum CreditRequests {
  Portfolio = 'Credit Portfolio(s)',
  SelectedPortfolio = 'Selected Credit Portfolio',
  GsePortfolio = 'GSE Portfolio(s)',
  CreditStructure = 'Credit Structure(s)',
  GseStructure = 'GSE Structure(s)',
  CreditStructureAdmin = 'Credit Admin Structure(s)',
  SelectedCreditStructure = 'Selected Credit Structure',
  CalculationStructure = 'Credit Calculation Structure(s)',
  GseCalculationStructure = 'GSE Calculation Structure(s)',
  SelectedCalculationStructure = 'Selected Credit Calculation Structure',
  CreditResults = 'Credit Results',
  SimulatedResults = 'Credit Simulated Results',
  DeterministicResults = 'Credit Deterministic Results',
  CashFlows = 'Credit Cash Flows',
  AnimatedScenarios = 'Credit Animated Scenarios Data',
  CreditStructureGroup = 'Credit Structure Group(s)',
  SelectedCreditStructureGroup = 'Selected Credit Structure Group',
  CreditStructureGroupMember = 'Credit Structure Group Member(s)',
  SelectedCreditStructureGroupMember = 'Selected Credit Structure Group Member',
  GroupCalculationStructure = 'Group Credit Calculation Structure(s)',
  GseStructureGroup = 'GSE Structure Group(s)',
  SelectedGseStructureGroup = 'Selected GSE Structure Group',
  GseStructureGroupMember = 'GSE Structure Group Member(s)',
  SelectedGseStructureGroupMember = 'Selected GSE Structure Group Member',
  GseGroupCalculationStructure = 'Group GSE Calculation Structure(s)',
  CreditStructureOptimize = 'Credit Structure Optimize',
  CreditOptimizerResults = 'Credit Optimization Results',
  CompareMetrics = 'Credit Compare Metrics',
  MetricSettings = 'Credit Metric Setting(s)',
  CarrierMetricConfigurations = 'Carrier Metric Configuration(s)',
  Stratification = 'Credit Stratification',
  StratificationFilter = 'Credit Stratification Filter',
  Performance = 'Credit Performance',
  Metrics = 'Credit Metrics',
  CreditSubmissionStructure = 'Credit Submission Structure(s)',
  GseSubmissionStructure = 'GSE Submission Structure(s)',
  SelectedCreditSubmissionStructure = 'Selected Credit Submission Structure',
  UserIdsOfCreditSubmissionStructure = 'User Ids of Credit Submission Structure',
  CreditSubmissionReinsurer = 'Credit Submission Reinsurer(s)',
  CreditSubmissionReinsurerStatus = 'Credit Submission Reinsurer Status',
  CreditSubmissionUserTranche = 'Credit Submission User Tranche(s)',
  GseSubmissionReinsurer = 'GSE Submission Reinsurer(s)',
  GseSubmissionReinsurerStatus = 'GSE Submission Reinsurer Status',
  GseSubmissionUserTranche = 'GSE Submission User Tranche(s)',
  CreditQuoteSubmissionAuthorization = 'Credit Quote Submit Quote',
  CreditQuoteAuditTable = 'Credit Quote Audit Table View',
  CreditQuoteReinsurerOverview = 'Credit Quote Reinsurer Overview',
  CreditQuoteAssignedLines = 'Credit Quote Assigned Lines',
  Upload = 'Upload',
  DownloadCsv = 'Download Csv',
  DownloadBlob = 'Download Blob',
  CreditModelScenarioMapping = 'Credit Model Scenario Mapping(s)',
  CreditIssueFot = 'Credit Issue FOT',
  CreditIssueAuthorization = 'Credit Issue Authorization',
  CreditUpdateAssignedLine = 'Credit Put Assigned Line',
  GetCreditReinsurers = 'Credit Get Credit Reinsurers',
  CreditQuotePostAssignedLine = 'Credit Post Assigned Line',
  CreditQuoteUploadAuthDoc = 'Credit Quote Upload Authorization Change Documentation',
  CreditQuoteDownloadAuthDoc = 'Credit Quote Download Authorization Change Documentation',
  CreditQuoteDeleteAssignedLine = 'Credit Delete Assigned Line',
  CreditQuoteInformReinsurersOfSignedPercentage = 'Credit Quote Inform Reinsurers of Signed %',
  CreditQuoteDecline = 'Credit Quote Decline',
  SendEmail = 'Send Email',
  CreditModelSimulate = 'Credit Model Simulate',
  CreditModelRunStatus = 'Credit Model Run Status',
  CreditModelInputHrefFromBlob = 'Credit Model Input Href(s) From Blob',
  CreditModelStats = 'Credit Model Stats',
  CreditModelMillimanSimulate = 'Credit Model Milliman Simulate',
  TransferBlobs = 'Transfer Blobs',
  TransferStochastic = 'Transfer Stochastic',
  CreditModelScenarioFileData = 'Credit Model Scenario File Data',
}

enum CreditApiRoutes {
  Portfolio = '/portfolio',
  CreditPortfolio = '/credit-portfolio',
  Structure = '/structure',
  Admin = '/admin',
  LayerResults = '/layer-results',
  Stratifications = '/stratification',
  StratificationsFilters = '/stratification-filters',
  SimulatedCreditResults = '/simulatedCreditResults',
  DeterministicCreditResults = '/deterministicCreditResults',
  CashFlows = '/cashFlows',
  AnimatedScenarios = '/animatedScenarios',
  CompareMetrics = '/compareMetrics',
  Performance = '/performance',
  Upload = '/upload',
  Submission = '/submission',
  AssignedLines = '/assigned-lines',
  Notify = '/notify',
  SignedPercent = '/signed-percent',
  Reinsurers = '/reinsurers',
  Tranche = '/tranche',
  TrancheSummary = '/tranche-summary',
  Audit = '/audit',
  ReinsurerOverview = '/reinsurer-overview',
  Overview = '/overview',
  Quote = '/quote',
  Decline = '/decline',
  IssueFot = '/issue-fot',
  IssueAuthorization = '/issue-authorization',
  Group = '/group',
  Carrier = '/carrier',
  Docs = '/docs',
  Status = '/status',
  Users = '/users',
  Simulate = '/simulate',
  LeapsSimulate = '/leaps-simulate',
  LeapsSimulateFromBlob = '/leaps-simulate-from-blob',
  MillimanSimulateFromBlob = '/milliman-simulate-from-blob',
  ScenarioFileData = '/scenario-file-data',
  LoanStats = '/loan-stats',
  Runs = '/runs',
  UploadCsv = '/upload-csv',
  FromBlob = '/from-blob',
  DownloadBlob = '/download-blob',
  DownloadCsv = '/download-csv',
  DownloadClsCsv = '/download-cls-csv',
  Transfer = '/transfer',
  TransferStochastic = '/transfer-stochastic',
}

enum CreditApiParams {
  PortfolioId = 'portfolioId',
  ProgramId = 'programId',
  Years = 'years',
  GroupId = 'groupId',
  StructureId = 'structureId',
  TransactionId = 'transactionId',
  TrancheId = 'trancheId',
  ScenarioMappingId = 'smId',
  ScenarioMappingIdFull = 'scenarioMappingId',
  ReturnPeriod = 'returnPeriod',
  Period = 'period', // TODO: remove this for consistency
  GrossLossScenarioId = 'grossLossScenarioId',
  ColType = 'colType',
  RowType = 'rowType',
  States = 'states',
  Field = 'field',
  GrossLossScenarioIds = 'grossLossScenarioIds',
  ActualLossScenarioIds = 'actualLossScenarioIds',
  ActualGLSIds = 'actualGLSIds',
  ActualGLSId = 'actualGLSId',
  IsActual = 'isActual',
  IsSubmission = 'isSubmission',
  IsPercent = 'isPercent',
  FileType = 'fileType',
  SubmissionId = 'submissionId',
  UserId = 'userId',
  CarrierId = 'carrierId',
  MetricConfigurationName = 'name',
  Statuses = 'statuses',
  BlobPath = 'blobPath',
  RunId = 'runId',
  PageSize = 'pageSize',
  PageNumber = 'pageNumber',
  StatusFilter = 'statusFilter',
  CallType = 'callType',
}

enum CreditProblemDetail {
  GroupMember = 'Group Member',
  Submission = 'Submission',
}

@Injectable({
  providedIn: 'root',
})
export class CreditService {
  constructor(
    private http: HttpClient,
    private snackbar: MatSnackBar,
    private confirmation: ConfirmationDialogService,
  ) {}

  /* using locktonre-sage-crd-api -> retrieves from mongo db database */
  getPortfolios(): ApiResponse<CreditPortfolio[]> {
    const endpoint = `${API.creditPortfolio}`
    return this.getEntity<CreditPortfolio[]>(endpoint, CreditRequests.Portfolio)
  }
  getPortfolioById(portfolioId: string): ApiResponse<CreditPortfolio> {
    const endpoint = `${API.creditPortfolio}/${portfolioId}`
    return this.getEntity<CreditPortfolio>(endpoint, CreditRequests.SelectedPortfolio)
  }
  postPortfolio(portfolio: CreditPortfolio): ApiResponse<CreditPortfolio> {
    const endpoint = `${API.creditPortfolio}`
    return this.postEntity<CreditPortfolio>(portfolio, endpoint, CreditRequests.Portfolio)
  }
  putPortfolio(portfolio: CreditPortfolio): ApiResponse<CreditPortfolio> {
    const endpoint = `${API.creditPortfolio}/${portfolio._id}`
    return this.putEntity<CreditPortfolio>(portfolio, endpoint, CreditRequests.Portfolio)
  }
  getGsePortfolios(): ApiResponse<CreditPortfolio[]> {
    const endpoint = `${API.gsePortfolio}`
    return this.getEntity<CreditPortfolio[]>(endpoint, CreditRequests.GsePortfolio)
  }
  getGseAccessiblePortfolioIds(): ApiResponse<string[]> {
    const endpoint = `${API.gsePortfolio}/portfolio-ids`
    return this.getEntity<string[]>(endpoint, CreditRequests.GsePortfolio)
  }
  getGsePortfolioById(portfolioId: string): ApiResponse<CreditPortfolio> {
    const endpoint = `${API.gsePortfolio}/${portfolioId}`
    return this.getEntity<CreditPortfolio>(endpoint, CreditRequests.GsePortfolio)
  }
  postGsePortfolio(portfolio: GsePortfolio): ApiResponse<GsePortfolio> {
    const endpoint = `${API.gsePortfolio}`
    return this.postEntity<GsePortfolio>(portfolio, endpoint, CreditRequests.GsePortfolio)
  }
  putGsePortfolio(portfolio: GsePortfolio): ApiResponse<GsePortfolio> {
    const endpoint = `${API.gsePortfolio}/${portfolio._id}`
    return this.putEntity<GsePortfolio>(portfolio, endpoint, CreditRequests.GsePortfolio)
  }
  deletePortfolio(id: string): ApiResponse<CreditPortfolio> {
    const endpoint = `${API.creditPortfolio}/${id}`
    return this.deleteEntity<CreditPortfolio>(endpoint, CreditRequests.Portfolio)
  }
  deleteGsePortfolio(id: string): ApiResponse<GsePortfolio> {
    const endpoint = `${API.gsePortfolio}/${id}`
    return this.deleteEntity<GsePortfolio>(endpoint, CreditRequests.GsePortfolio)
  }
  /**
   * Transfers blobs between Azure storage containers
   *
   * The main container for credit calculations is `credit-sage`, but files from LEAPs modeling
   * are stored under a different container (`leaps-api-data`). This method copies files from
   * the LEAPs container to the credit-sage container so they can be used in credit calculations.
   *
   * @param sourceHrefs - Array of source blob URLs to transfer
   * @returns Observable containing the transfer results with source and destination hrefs
   */
  transferStochastic(sourceHref: string): ApiResponse<CreditPortfolioUploadResults> {
    const url = `${API.base}${API.creditPortfolio}${CreditApiRoutes.TransferStochastic}`
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.http
      .post<CreditPortfolioUploadResults>(url, JSON.stringify(sourceHref), { headers })
      .pipe(mapToMaybeData(), catchAndHandleError(`POST ${CreditRequests.TransferStochastic}`))
  }
  transferBlobsBetweenContainers(sourceHrefs: string[]): ApiResponse<BlobTransferResult[]> {
    const endpoint = `${API.creditPortfolio}${CreditApiRoutes.Transfer}`
    return this.postEntityWithResponse<string[], BlobTransferResult[]>(
      sourceHrefs,
      endpoint,
      CreditRequests.TransferBlobs,
    )
  }
  uploadPortfolioData(
    fileType: CreditFileType,
    file: FormData,
  ): ApiResponse<CreditPortfolioUploadResults> {
    const queryParam = this.buildQueryParam(CreditApiParams.FileType, fileType)
    const url = `${API.base}${API.creditPortfolio}${CreditApiRoutes.Upload}?${queryParam}`
    return this.http.post<CreditPortfolioUploadResults>(url, file).pipe(
      mapToMaybeData(),
      catchError((error: ProblemDetails) =>
        of({
          error: errorPayload(`${CreditRequests.Upload} Failed`, error.detail),
        }),
      ),
    )
  }
  downloadGrossFile(
    fileType: CreditFileType,
    portfolioId: string,
    grossLossScenarioId?: string, // required for gls, actuals_gls, pattach, loan, and stochastic
    returnPeriod?: string, // required for stochastic
  ): ApiResponse<BlobWithHeaders> {
    const params = [
      {
        key: CreditApiParams.PortfolioId,
        value: portfolioId,
      },

      { key: CreditApiParams.FileType, value: fileType },
    ]
    if (grossLossScenarioId) {
      params.push({
        key: CreditApiParams.GrossLossScenarioId,
        value: grossLossScenarioId,
      })
    }
    if (returnPeriod) {
      params.push({ key: CreditApiParams.ReturnPeriod, value: returnPeriod })
    }
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditPortfolio}${CreditApiRoutes.DownloadCsv}`
    return this.getBlobWithHeaders(endpoint, CreditRequests.DownloadCsv, queryParams)
  }
  downloadCededFile(
    structureId: string,
    transactionId: string,
    scenarioMappingId: string | undefined,
    trancheId?: string,
    isGse = false,
    grossLossScenarioId?: string, // GSE: identifies CLS by GLS _id instead of scenario_mapping_id
    returnPeriod?: string, // GSE stochastic
  ): ApiResponse<BlobWithHeaders> {
    const params = [
      { key: CreditApiParams.StructureId, value: structureId },
      { key: CreditApiParams.TransactionId, value: transactionId },
    ]
    if (grossLossScenarioId) {
      params.push({ key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId })
      if (returnPeriod) {
        params.push({ key: CreditApiParams.ReturnPeriod, value: returnPeriod })
      }
    } else if (scenarioMappingId) {
      params.push({ key: CreditApiParams.ScenarioMappingId, value: scenarioMappingId })
    }
    if (trancheId) {
      params.push({ key: CreditApiParams.TrancheId, value: trancheId })
    }
    const queryParams = this.buildQueryParams(params)
    // Both MI (CreditCalculationController) and GSE (GseCalculationController) expose the same route path
    const base = isGse ? API.gseCalculation : API.creditCalculation
    const endpoint = `${base}${CreditApiRoutes.DownloadClsCsv}`
    return this.getBlobWithHeaders(endpoint, CreditRequests.DownloadCsv, queryParams)
  }
  /**
   * Downloads a gross file that was imported from a modeling run
   * @param blobPath The path to the blob file
   * @returns The blob file with headers
   */
  downloadGrossFileFromModeling(blobPath: string): ApiResponse<BlobWithHeaders> {
    const params = [
      {
        key: CreditApiParams.BlobPath,
        value: blobPath,
      },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditModel}${CreditApiRoutes.DownloadBlob}`
    return this.getBlobWithHeaders(endpoint, CreditRequests.DownloadBlob, queryParams)
  }
  getCalculationStructures(): ApiResponse<CreditCalculationStructure[]> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}`
    return this.getEntity<CreditCalculationStructure[]>(
      endpoint,
      CreditRequests.CalculationStructure,
    )
  }
  getCreditAdminStructures(): ApiResponse<CreditAdminCalculationStructure[]> {
    const endpoint = `${API.creditStructure}${CreditApiRoutes.Admin}`
    return this.getEntity<CreditAdminCalculationStructure[]>(
      endpoint,
      CreditRequests.CreditStructure,
    )
  }
  deleteCreditStructureById(id: string): ApiResponse<CreditStructure> {
    const endpoint = `${API.creditStructure}/${id}`
    const url = `${API.base}${endpoint}`
    return this.http.delete<CreditStructure>(url).pipe(
      mapToMaybeData(),
      catchError((error: ProblemDetails) => {
        if (
          error.detail?.includes(CreditProblemDetail.GroupMember) ||
          error.detail?.includes(CreditProblemDetail.Submission)
        ) {
          this.snackbar.openFromComponent(ErrorComponent, {
            data: errorPayload(error.detail),
            panelClass: 'app-error',
            duration: 10000,
          })
        }
        return throwError(() => error)
      }),
    )
  }
  getCalculationStructureById(id: string): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${id}`
    return this.getEntity<CreditCalculationStructure>(
      endpoint,
      CreditRequests.SelectedCalculationStructure,
    )
  }
  postCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}`
    return this.postEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.CalculationStructure,
    )
  }
  putCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structure._id}`
    return this.putEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.CalculationStructure,
    )
  }
  getGseCalculationStructureById(id: string): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${id}`
    return this.getEntity<CreditCalculationStructure>(
      endpoint,
      CreditRequests.GseCalculationStructure,
    )
  }
  postGseCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}`
    return this.postEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GseCalculationStructure,
    )
  }
  putGseCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structure._id}`
    return this.putEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GseCalculationStructure,
    )
  }
  postGroupCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Group}`
    return this.postEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GroupCalculationStructure,
    )
  }
  putGroupCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Group}/${structure._id}`
    return this.putEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GroupCalculationStructure,
    )
  }
  getCreditResults(
    structureId: string,
    grossLossScenarioId: string,
    returnPeriod?: number,
    callType?: CallTypeSelector,
  ): ApiResponse<CreditResults> {
    const kvps = [
      {
        key: CreditApiParams.GrossLossScenarioId,
        value: grossLossScenarioId,
      },
    ]
    if (returnPeriod) {
      kvps.push({ key: CreditApiParams.ReturnPeriod, value: String(returnPeriod) })
    }
    if (callType) {
      kvps.push({
        key: CreditApiParams.CallType,
        value: callType,
      })
    }
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.LayerResults}`
    return this.getEntity<CreditResults>(endpoint, CreditRequests.CreditResults, queryParams)
  }
  getCreditStratificationFilters(portfolioId: string): ApiResponse<StratificationFilters> {
    const endpoint = `${CreditApiRoutes.CreditPortfolio}/${portfolioId}${CreditApiRoutes.StratificationsFilters}`
    return this.getEntity<StratificationFilters>(endpoint, CreditRequests.StratificationFilter)
  }
  getCreditStratificationResults(
    portfolioId: string,
    rowType: StratificationAxisField,
    colType: StratificationAxisField,
    optionals?: {
      states?: string[]
      filters?: StratificationFilters
    },
  ): ApiResponse<StratificationResults> {
    const paramList: { key: string; value: string | number }[] = [
      { key: CreditApiParams.RowType, value: rowType },
      { key: CreditApiParams.ColType, value: colType },
    ]
    if (optionals.states) {
      paramList.push({
        key: CreditApiParams.States,
        value: optionals.states.join(','),
      })
    }
    if (optionals.filters) {
      for (let [key, value] of Object.entries(optionals.filters)) {
        ;(value as string[]).forEach((option) => {
          paramList.push({
            key,
            value: option,
          })
        })
      }
    }
    const queryParams = this.buildQueryParams(paramList)
    const endpoint = `${CreditApiRoutes.CreditPortfolio}/${portfolioId}${CreditApiRoutes.Stratifications}`
    return this.getEntity<StratificationResults>(
      endpoint,
      CreditRequests.Stratification,
      queryParams,
    )
  }
  getPerformanceResults(
    structureId: string,
    field: PerformanceField,
    grossLossIds: string[],
    actualLossIds: string[],
  ): ApiResponse<PerformanceResults> {
    const rowsAndCols: { key: string; value: string | number }[] = [
      { key: CreditApiParams.Field, value: field },
      {
        key: CreditApiParams.GrossLossScenarioIds,
        value: grossLossIds.join(','),
      },
      {
        key: CreditApiParams.ActualLossScenarioIds,
        value: actualLossIds.join(','),
      },
    ]
    const queryParams = this.buildQueryParams(rowsAndCols)
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.Performance}`

    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`

    return this.http.get<PerformanceResults>(url).pipe(
      mapToMaybeData(),
      catchError((error) => {
        const details = error.detail as string
        if (details) {
          if (details.toLowerCase().includes('actual loan data not found')) {
            this.confirmation.open({
              message:
                'Missing actuals data. Upload actual data to view this graph with actual data.',
              submitLabel: 'Ok',
              hideCancel: true,
            })
          }
          this.snackbar.openFromComponent(ErrorComponent, {
            data: errorPayload(details),
            panelClass: 'app-error',
            duration: 10000,
          })
        }
        return throwError(() => error)
      }),
    )
  }
  getCreditSimulatedResults(
    structureId: string,
    grossLossScenarioId: string,
    years: number,
  ): ApiResponse<StructureResultGroup> {
    const kvps = [
      {
        key: CreditApiParams.GrossLossScenarioId,
        value: grossLossScenarioId,
      },
      { key: CreditApiParams.Period, value: years }, // TODO: update this param to returnPeriod for consistency
    ]
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.SimulatedCreditResults}`
    return this.getEntity<StructureResultGroup>(
      endpoint,
      CreditRequests.SimulatedResults,
      queryParams,
    )
  }
  getCreditDeterministicResults(
    structureId: string,
    grossLossScenarioId: string,
  ): ApiResponse<StructureResultGroup> {
    const queryParam = this.buildQueryParam(
      CreditApiParams.GrossLossScenarioId,
      grossLossScenarioId,
    )
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.DeterministicCreditResults}`
    return this.getEntity<StructureResultGroup>(
      endpoint,
      CreditRequests.DeterministicResults,
      queryParam,
    )
  }
  getCreditCashFlows(
    structureId: string,
    grossLossScenarioId: string,
    transactionId: string,
    isActual: boolean,
    isSubmission: boolean,
    isPercent: boolean,
    returnPeriod?: number,
    callType?: CallTypeSelector,
  ): ApiResponse<CashFlowsData> {
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.CashFlows}`
    const kvps = [
      {
        key: CreditApiParams.GrossLossScenarioId,
        value: grossLossScenarioId,
      },
      { key: CreditApiParams.TransactionId, value: transactionId },
      { key: CreditApiParams.IsActual, value: String(isActual) },
      {
        key: CreditApiParams.IsSubmission,
        value: String(isSubmission),
      },
      {
        key: CreditApiParams.IsPercent,
        value: String(isPercent),
      },
    ]
    if (returnPeriod) {
      kvps.push({
        key: CreditApiParams.ReturnPeriod,
        value: String(returnPeriod),
      })
    }
    if (callType) {
      kvps.push({
        key: CreditApiParams.CallType,
        value: callType,
      })
    }
    const queryParams = this.buildQueryParams(kvps)

    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`

    return this.http.get<CashFlowsData>(url).pipe(
      mapToMaybeData(),
      catchError((error) => {
        const details = error.detail as string
        if (details) {
          if (details.toLowerCase().includes('actual loan data not found')) {
            this.confirmation.open({
              message:
                'Missing actuals data. Upload actual data to view this graph with actual data.',
              submitLabel: 'Ok',
              hideCancel: true,
            })
          }
          this.snackbar.openFromComponent(ErrorComponent, {
            data: errorPayload(details),
            panelClass: 'app-error',
            duration: 10000,
          })
        }
        return throwError(() => error)
      }),
    )
  }
  getGseResults(
    structureId: string,
    grossLossScenarioId: string,
    returnPeriod?: number,
    callType?: CallTypeSelector,
  ): ApiResponse<CreditResults> {
    const kvps = [{ key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId }]
    if (returnPeriod) {
      kvps.push({ key: CreditApiParams.ReturnPeriod, value: String(returnPeriod) })
    }
    if (callType) {
      kvps.push({ key: CreditApiParams.CallType, value: callType })
    }
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.LayerResults}`
    return this.getEntity<CreditResults>(endpoint, CreditRequests.CreditResults, queryParams)
  }
  getGseDeterministicResults(
    structureId: string,
    grossLossScenarioId: string,
  ): ApiResponse<StructureResultGroup> {
    const queryParam = this.buildQueryParam(
      CreditApiParams.GrossLossScenarioId,
      grossLossScenarioId,
    )
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.DeterministicCreditResults}`
    return this.getEntity<StructureResultGroup>(
      endpoint,
      CreditRequests.DeterministicResults,
      queryParam,
    )
  }
  getGseSimulatedResults(
    structureId: string,
    grossLossScenarioId: string,
    years: number,
  ): ApiResponse<StructureResultGroup> {
    const kvps = [
      { key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId },
      { key: CreditApiParams.Period, value: years },
    ]
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.SimulatedCreditResults}`
    return this.getEntity<StructureResultGroup>(
      endpoint,
      CreditRequests.SimulatedResults,
      queryParams,
    )
  }
  getGseCashFlows(
    structureId: string,
    grossLossScenarioId: string,
    transactionId: string,
    isActual: boolean,
    isPercent: boolean,
    returnPeriod?: number,
    callType?: CallTypeSelector,
    isSubmission?: boolean,
  ): ApiResponse<CashFlowsData> {
    const kvps = [
      { key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId },
      { key: CreditApiParams.TransactionId, value: transactionId },
      { key: CreditApiParams.IsActual, value: String(isActual) },
      { key: CreditApiParams.IsPercent, value: String(isPercent) },
    ]
    if (returnPeriod) {
      kvps.push({ key: CreditApiParams.ReturnPeriod, value: String(returnPeriod) })
    }
    if (callType) {
      kvps.push({ key: CreditApiParams.CallType, value: callType })
    }
    if (isSubmission) {
      kvps.push({ key: CreditApiParams.IsSubmission, value: String(isSubmission) })
    }
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.CashFlows}`
    return this.getEntity<CashFlowsData>(endpoint, CreditRequests.CashFlows, queryParams)
  }

  getAnimatedScenariosData(
    structureId: string,
    grossLossScenarioId: string,
    actualGLSId: string,
    isSubmission: boolean,
    returnPeriod: string,
  ): ApiResponse<AnimatedScenariosResults[]> {
    const params: { key: string; value: string | boolean }[] = [
      {
        key: CreditApiParams.IsSubmission,
        value: isSubmission,
      },
      { key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId },
      { key: CreditApiParams.ActualGLSId, value: actualGLSId },
    ]
    if (returnPeriod) {
      params.push({ key: CreditApiParams.ReturnPeriod, value: returnPeriod })
    }
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.AnimatedScenarios}`
    return this.getEntityWithMapping<AnimatedScenariosResults[], AnimatedScenariosResultsData>(
      endpoint,
      CreditRequests.AnimatedScenarios,
      convertAnimatedScenariosData,
      queryParams,
    )
  }

  getGseAnimatedScenariosData(
    structureId: string,
    grossLossScenarioId: string,
    actualGLSId: string,
    returnPeriod: string,
    callType?: CallTypeSelector,
  ): ApiResponse<AnimatedScenariosResults[]> {
    const params: { key: string; value: string | boolean }[] = [
      { key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId },
      { key: CreditApiParams.ActualGLSId, value: actualGLSId },
    ]
    if (returnPeriod) {
      params.push({ key: CreditApiParams.ReturnPeriod, value: returnPeriod })
    }
    if (callType) {
      params.push({ key: CreditApiParams.CallType, value: callType })
    }
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.AnimatedScenarios}`
    return this.getEntityWithMapping<AnimatedScenariosResults[], AnimatedScenariosResultsData>(
      endpoint,
      CreditRequests.AnimatedScenarios,
      convertAnimatedScenariosData,
      queryParams,
    )
  }

  getCompareMetricsForStructure(
    structureId: string,
    carrierId: number,
    name: string,
  ): ApiResponse<CreditCompareMetricResponse> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      { key: CreditApiParams.MetricConfigurationName, value: name },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.CompareMetrics}`
    return this.getEntity<CreditCompareMetricResponse>(
      endpoint,
      CreditRequests.CompareMetrics,
      queryParams,
    )
  }

  getGseCompareMetricsForStructure(
    structureId: string,
    carrierId: number,
    name: string,
  ): ApiResponse<CreditCompareMetricResponse> {
    const params = [
      { key: CreditApiParams.CarrierId, value: carrierId },
      { key: CreditApiParams.MetricConfigurationName, value: name },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}/${structureId}${CreditApiRoutes.CompareMetrics}`
    return this.getEntity<CreditCompareMetricResponse>(
      endpoint,
      CreditRequests.CompareMetrics,
      queryParams,
    )
  }

  /* using locktonre-sage-services-standalone -> retrieves from SAGE sql server database */
  getCreditStructureByCalculationStructureId(structureId: string): ApiResponse<CreditStructure> {
    const endpoint = `${API.creditStructure}/${structureId}`
    return this.getEntity<CreditStructure>(
      endpoint,
      CreditRequests.SelectedCreditStructure,
    ) /* by credit_calculation_structure_id */
  }
  getCreditStructuresByProgramId(programId: number): ApiResponse<CreditStructure[]> {
    const endpoint = `${API.creditStructure}`
    const queryParam = this.buildQueryParam(CreditApiParams.ProgramId, programId)
    return this.getEntity<CreditStructure[]>(endpoint, CreditRequests.CreditStructure, queryParam)
  }
  getGseStructuresByProgramId(programId: number): ApiResponse<GseStructure[]> {
    const endpoint = `${API.gseStructure}`
    const queryParam = this.buildQueryParam(CreditApiParams.ProgramId, programId)
    return this.getEntity<GseStructure[]>(endpoint, CreditRequests.GseStructure, queryParam)
  }
  postCreditStructure(structure: CreditStructure): ApiResponse<CreditStructure> {
    const endpoint = `${API.creditStructure}`
    return this.postEntity<CreditStructure>(structure, endpoint, CreditRequests.CreditStructure)
  }
  putCreditStructure(structure: CreditStructure): ApiResponse<CreditStructure> {
    const endpoint = `${API.creditStructure}/${structure.id}`
    return this.putEntity<CreditStructure>(structure, endpoint, CreditRequests.CreditStructure)
  }
  getGseStructureByCalculationStructureId(structureId: string): ApiResponse<GseStructure> {
    const endpoint = `${API.gseStructure}/${structureId}`
    return this.getEntity<GseStructure>(endpoint, CreditRequests.GseStructure)
  }
  postGseStructure(structure: GseStructure): ApiResponse<GseStructure> {
    const endpoint = `${API.gseStructure}`
    return this.postEntity<GseStructure>(structure, endpoint, CreditRequests.GseStructure)
  }
  putGseStructure(structure: GseStructure): ApiResponse<GseStructure> {
    const endpoint = `${API.gseStructure}/${structure.id}`
    return this.putEntity<GseStructure>(structure, endpoint, CreditRequests.GseStructure)
  }
  getCreditStructureGroupByCalculationStructureId(
    structureId: string,
  ): ApiResponse<CreditStructureGroup> {
    const endpoint = `${API.creditStructureGroup}/${structureId}`
    return this.getEntity<CreditStructureGroup>(
      endpoint,
      CreditRequests.SelectedCreditStructureGroup,
    ) /* by credit_calculation_structure_id */
  }
  getCreditStructureGroupsByProgramId(programId: number): ApiResponse<CreditStructureGroup[]> {
    const endpoint = `${API.creditStructureGroup}`
    const queryParam = this.buildQueryParam(CreditApiParams.ProgramId, programId)
    return this.getEntity<CreditStructureGroup[]>(
      endpoint,
      CreditRequests.CreditStructureGroup,
      queryParam,
    )
  }
  postCreditStructureGroup(
    structureGroup: CreditStructureGroup,
  ): ApiResponse<CreditStructureGroup> {
    const endpoint = `${API.creditStructureGroup}`
    return this.postEntity<CreditStructureGroup>(
      structureGroup,
      endpoint,
      CreditRequests.CreditStructureGroup,
    )
  }
  putCreditStructureGroup(structureGroup: CreditStructureGroup): ApiResponse<CreditStructureGroup> {
    const endpoint = `${API.creditStructureGroup}/${structureGroup.id}`
    return this.putEntity<CreditStructureGroup>(
      structureGroup,
      endpoint,
      CreditRequests.CreditStructureGroup,
    )
  }
  deleteCreditStructureGroup(
    structureGroup: CreditStructureGroup,
  ): ApiResponse<CreditStructureGroup> {
    const endpoint = `${API.creditStructureGroup}/${structureGroup.id}`
    return this.deleteEntity<CreditStructureGroup>(endpoint, CreditRequests.CreditStructureGroup)
  }
  getCreditStructureGroupMembersByGroupId(
    groupId: number,
  ): ApiResponse<CreditStructureGroupMember[]> {
    const endpoint = `${API.creditStructureGroupMember}`
    const queryParam = this.buildQueryParam(CreditApiParams.GroupId, groupId)
    return this.getEntity<CreditStructureGroupMember[]>(
      endpoint,
      CreditRequests.SelectedCreditStructureGroupMember,
      queryParam,
    ) /* by credit_structure_group_id'*/
  }
  postCreditStructureGroupMember(
    structureGroupMember: CreditStructureGroupMember,
  ): ApiResponse<CreditStructureGroupMember> {
    const endpoint = `${API.creditStructureGroupMember}`
    return this.postEntity<CreditStructureGroupMember>(
      structureGroupMember,
      endpoint,
      CreditRequests.CreditStructureGroupMember,
    )
  }
  putCreditStructureGroupMember(
    structureGroupMember: CreditStructureGroupMember,
  ): ApiResponse<CreditStructureGroupMember> {
    const endpoint = `${API.creditStructureGroupMember}/${structureGroupMember.id}`
    return this.putEntity<CreditStructureGroupMember>(
      structureGroupMember,
      endpoint,
      CreditRequests.CreditStructureGroupMember,
    )
  }
  postGseGroupCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Group}`
    return this.postEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GseGroupCalculationStructure,
    )
  }
  putGseGroupCalculationStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Group}/${structure._id}`
    return this.putEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GseGroupCalculationStructure,
    )
  }
  getGseStructureGroupsByProgramId(programId: number): ApiResponse<GseStructureGroup[]> {
    const endpoint = `${API.gseGroup}`
    const queryParam = this.buildQueryParam(CreditApiParams.ProgramId, programId)
    return this.getEntity<GseStructureGroup[]>(
      endpoint,
      CreditRequests.GseStructureGroup,
      queryParam,
    )
  }
  getGseStructureGroupByCalculationStructureId(
    structureId: string,
  ): ApiResponse<GseStructureGroup> {
    const endpoint = `${API.gseGroup}/${structureId}`
    return this.getEntity<GseStructureGroup>(
      endpoint,
      CreditRequests.SelectedGseStructureGroup,
    ) /* by gse_calculation_structure_id */
  }
  postGseStructureGroup(structureGroup: GseStructureGroup): ApiResponse<GseStructureGroup> {
    const endpoint = `${API.gseGroup}`
    return this.postEntity<GseStructureGroup>(
      structureGroup,
      endpoint,
      CreditRequests.GseStructureGroup,
    )
  }
  putGseStructureGroup(structureGroup: GseStructureGroup): ApiResponse<GseStructureGroup> {
    const endpoint = `${API.gseGroup}/${structureGroup.id}`
    return this.putEntity<GseStructureGroup>(
      structureGroup,
      endpoint,
      CreditRequests.GseStructureGroup,
    )
  }
  deleteGseStructureGroup(structureGroup: GseStructureGroup): ApiResponse<GseStructureGroup> {
    const endpoint = `${API.gseGroup}/${structureGroup.id}`
    return this.deleteEntity<GseStructureGroup>(endpoint, CreditRequests.GseStructureGroup)
  }
  getGseStructureGroupMembersByGroupId(groupId: number): ApiResponse<GseStructureGroupMember[]> {
    const endpoint = `${API.gseGroupMember}`
    const queryParam = this.buildQueryParam(CreditApiParams.GroupId, groupId)
    return this.getEntity<GseStructureGroupMember[]>(
      endpoint,
      CreditRequests.SelectedGseStructureGroupMember,
      queryParam,
    ) /* by gse_structure_group_id */
  }
  postGseStructureGroupMember(
    structureGroupMember: GseStructureGroupMember,
  ): ApiResponse<GseStructureGroupMember> {
    const endpoint = `${API.gseGroupMember}`
    return this.postEntity<GseStructureGroupMember>(
      structureGroupMember,
      endpoint,
      CreditRequests.GseStructureGroupMember,
    )
  }
  putGseStructureGroupMember(
    structureGroupMember: GseStructureGroupMember,
  ): ApiResponse<GseStructureGroupMember> {
    const endpoint = `${API.gseGroupMember}/${structureGroupMember.id}`
    return this.putEntity<GseStructureGroupMember>(
      structureGroupMember,
      endpoint,
      CreditRequests.GseStructureGroupMember,
    )
  }
  deleteGseStructureGroupMember(id: number): ApiResponse<GseStructureGroupMember> {
    const endpoint = `${API.gseGroupMember}/${id}`
    return this.deleteEntity<GseStructureGroupMember>(
      endpoint,
      CreditRequests.GseStructureGroupMember,
    )
  }
  getCreditMetricConfigurationNamesByCarrierId(carrierId: number): ApiResponse<string[]> {
    // TODO: update API to be metrics, not compare metrics
    const endpoint = `${API.creditCompareMetrics}${CreditApiRoutes.Carrier}/${carrierId}`
    return this.getEntity<string[]>(endpoint, CreditRequests.CarrierMetricConfigurations)
  }
  getCreditMetricSettingsByCarrierAndName(
    carrierId: number,
    name: string,
  ): ApiResponse<CreditMetricSetting[]> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      { key: CreditApiParams.MetricConfigurationName, value: name },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditCompareMetrics}`
    return this.getEntity<CreditMetricSetting[]>(
      endpoint,
      CreditRequests.MetricSettings,
      queryParams,
    )
  }
  saveCreditMetricSettings(
    metricSettings: CreditMetricSetting[],
  ): ApiResponse<CreditMetricSetting[]> {
    const endpoint = `${API.creditCompareMetrics}`
    return this.postEntity<CreditMetricSetting[]>(
      metricSettings,
      endpoint,
      CreditRequests.MetricSettings,
    )
  }
  updateCreditMetricSettings(
    metricSettings: CreditMetricSetting[],
  ): ApiResponse<CreditMetricSetting[]> {
    const endpoint = `${API.creditCompareMetrics}`
    return this.putEntity<CreditMetricSetting[]>(
      metricSettings,
      endpoint,
      CreditRequests.MetricSettings,
    )
  }
  deleteCreditMetricSetting(id: string): ApiResponse<CreditMetricSetting> {
    const endpoint = `${API.creditCompareMetrics}/${id}`
    return this.deleteEntity(endpoint, CreditRequests.MetricSettings)
  }
  deleteCreditMetricSettingsByCarrierAndName(
    carrierId: number,
    name: string,
  ): ApiResponse<CreditMetricSetting[]> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      { key: CreditApiParams.MetricConfigurationName, value: name },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditCompareMetrics}?${queryParams}`
    return this.deleteEntity<CreditMetricSetting[]>(
      endpoint,
      CreditRequests.CarrierMetricConfigurations,
    )
  }
  getGseMetricConfigurationNamesByCarrierId(carrierId: number): ApiResponse<string[]> {
    const endpoint = `${API.gseCompareMetrics}${CreditApiRoutes.Carrier}/${carrierId}`
    return this.getEntity<string[]>(endpoint, CreditRequests.CarrierMetricConfigurations)
  }
  getGseMetricSettingsByCarrierAndName(
    carrierId: number,
    name: string,
  ): ApiResponse<CreditMetricSetting[]> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      { key: CreditApiParams.MetricConfigurationName, value: name },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.gseCompareMetrics}`
    return this.getEntity<CreditMetricSetting[]>(
      endpoint,
      CreditRequests.MetricSettings,
      queryParams,
    )
  }
  saveGseMetricSettings(metricSettings: CreditMetricSetting[]): ApiResponse<CreditMetricSetting[]> {
    const endpoint = `${API.gseCompareMetrics}`
    return this.postEntity<CreditMetricSetting[]>(
      metricSettings,
      endpoint,
      CreditRequests.MetricSettings,
    )
  }
  updateGseMetricSettings(
    metricSettings: CreditMetricSetting[],
  ): ApiResponse<CreditMetricSetting[]> {
    const endpoint = `${API.gseCompareMetrics}`
    return this.putEntity<CreditMetricSetting[]>(
      metricSettings,
      endpoint,
      CreditRequests.MetricSettings,
    )
  }
  deleteGseMetricSetting(id: string): ApiResponse<CreditMetricSetting> {
    const endpoint = `${API.gseCompareMetrics}/${id}`
    return this.deleteEntity(endpoint, CreditRequests.MetricSettings)
  }
  deleteGseMetricSettingsByCarrierAndName(
    carrierId: number,
    name: string,
  ): ApiResponse<CreditMetricSetting[]> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      { key: CreditApiParams.MetricConfigurationName, value: name },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.gseCompareMetrics}?${queryParams}`
    return this.deleteEntity<CreditMetricSetting[]>(
      endpoint,
      CreditRequests.CarrierMetricConfigurations,
    )
  }
  // TODO: change any to string (processId)
  postCalculateOptimizedStructure(
    creditStructureOptimizationInput: CreditStructureOptimizationInput,
    carrierId: number,
    metricConfigurationName: string,
  ): ApiResponse<any> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      {
        key: CreditApiParams.MetricConfigurationName,
        value: metricConfigurationName,
      },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.creditCalculation}${CreditApiRoutes.Structure}${API.creditStructureOptimize}`
    return this.postEntity<CreditStructureOptimizationInput>(
      creditStructureOptimizationInput,
      endpoint,
      CreditRequests.CreditStructureOptimize,
      queryParams,
    )
  }
  postCalculateGseOptimizedStructure(
    creditStructureOptimizationInput: CreditStructureOptimizationInput,
    carrierId: number,
    metricConfigurationName: string,
  ): ApiResponse<any> {
    const params = [
      {
        key: CreditApiParams.CarrierId,
        value: carrierId,
      },
      {
        key: CreditApiParams.MetricConfigurationName,
        value: metricConfigurationName,
      },
    ]
    const queryParams = this.buildQueryParams(params)
    const endpoint = `${API.gseCalculation}${CreditApiRoutes.Structure}${API.creditStructureOptimize}`
    return this.postEntity<CreditStructureOptimizationInput>(
      creditStructureOptimizationInput,
      endpoint,
      CreditRequests.CreditStructureOptimize,
      queryParams,
    )
  }
  getOptimizationResponse(processId: string): ApiResponse<CreditOptimizationResponse> {
    const endpoint = `${API.creditCalculation}${API.creditOptimizer}/${processId}`
    return this.getEntity<CreditOptimizationResponse>(
      endpoint,
      CreditRequests.CreditOptimizerResults,
    )
  }
  getGseOptimizationResponse(processId: string): ApiResponse<CreditOptimizationResponse> {
    const endpoint = `${API.gseCalculation}${API.creditOptimizer}/${processId}`
    return this.getEntity<CreditOptimizationResponse>(
      endpoint,
      CreditRequests.CreditOptimizerResults,
    )
  }
  getCreditSubmissionStructureBySubmissionStructureId(
    structureId: string,
  ): ApiResponse<CreditSubmissionStructure> {
    const endpoint = `${API.creditSubmissionStructure}/${structureId}`
    return this.getEntity<CreditSubmissionStructure>(
      endpoint,
      CreditRequests.SelectedCreditSubmissionStructure,
    ) /* by credit_submission_structure_id */
  }
  getCreditSubmissionStructures(
    years: string[],
    status: CreditSubmissionStatusCombined[] = [CreditSubmissionStatus.Released],
  ): ApiResponse<CreditSubmissionStructureWithProgramData[]> {
    const endpoint = `${API.creditSubmissionStructure}`
    const kvps = [
      {
        key: CreditApiParams.Years,
        value: years.join(','),
      },
      { key: CreditApiParams.Statuses, value: status.join(',') },
    ]
    const queryParams = this.buildQueryParams(kvps)
    return this.getEntity<CreditSubmissionStructureWithProgramData[]>(
      endpoint,
      CreditRequests.CreditSubmissionStructure,
      queryParams,
    )
  }
  getGseSubmissionStructures(
    years: string[],
    status: GseSubmissionStatus[] = [GseSubmissionStatus.Released],
  ): ApiResponse<GseSubmissionStructure[]> {
    const endpoint = `${API.gseSubmissionStructure}`
    const kvps = [
      { key: CreditApiParams.Years, value: years.join(',') },
      { key: CreditApiParams.Statuses, value: status.join(',') },
    ]
    const queryParams = this.buildQueryParams(kvps)
    return this.getEntity<GseSubmissionStructure[]>(
      endpoint,
      CreditRequests.GseSubmissionStructure,
      queryParams,
    )
  }
  postCreditSubmissionStructure(
    structure: CreditSubmissionStructure,
  ): ApiResponse<CreditSubmissionStructure> {
    const endpoint = `${API.creditSubmissionStructure}`
    return this.postEntity<CreditSubmissionStructure>(
      structure,
      endpoint,
      CreditRequests.CreditSubmissionStructure,
    )
  }
  postGseSubmissionStructure(
    submission: GseSubmissionStructure,
  ): ApiResponse<GseSubmissionStructure> {
    const endpoint = `${API.gseSubmissionStructure}`
    return this.postEntity<GseSubmissionStructure>(
      submission,
      endpoint,
      CreditRequests.GseSubmissionStructure,
    )
  }
  putCreditSubmissionStructure(
    structure: CreditSubmissionStructure,
  ): ApiResponse<CreditSubmissionStructure> {
    const endpoint = `${API.creditSubmissionStructure}/${structure.id}`
    return this.putEntity<CreditSubmissionStructure>(
      structure,
      endpoint,
      CreditRequests.CreditSubmissionStructure,
    )
  }
  getGseSubmissionResults(
    structureId: string,
    grossLossScenarioId: string,
    isActual: boolean,
    returnPeriod?: number,
    callType?: CallTypeSelector,
  ): ApiResponse<CreditResults> {
    const kvps = [
      { key: CreditApiParams.GrossLossScenarioId, value: grossLossScenarioId },
      { key: CreditApiParams.IsActual, value: String(isActual) },
    ]
    if (returnPeriod) {
      kvps.push({ key: CreditApiParams.ReturnPeriod, value: String(returnPeriod) })
    }
    if (callType) {
      kvps.push({ key: CreditApiParams.CallType, value: callType })
    }
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.gseSubmissionStructure}/${structureId}${CreditApiRoutes.LayerResults}`
    return this.getEntity<CreditResults>(endpoint, CreditRequests.CreditResults, queryParams)
  }

  getCreditSubmissionResults(
    structureId: string,
    grossLossScenarioId: string,
    isActual: boolean,
    returnPeriod?: number,
    callType?: CallTypeSelector,
  ): ApiResponse<CreditResults> {
    const kvps = [
      {
        key: CreditApiParams.GrossLossScenarioId,
        value: grossLossScenarioId,
      },
      { key: CreditApiParams.IsActual, value: String(isActual) },
    ]
    if (returnPeriod) {
      kvps.push({ key: CreditApiParams.ReturnPeriod, value: String(returnPeriod) })
    }
    if (callType) {
      kvps.push({
        key: CreditApiParams.CallType,
        value: callType,
      })
    }
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.creditSubmissionStructure}/${structureId}${CreditApiRoutes.LayerResults}`
    return this.getEntity<CreditResults>(endpoint, CreditRequests.CreditResults, queryParams)
  }
  createSubmissionStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditSubmissionStructure}${CreditApiRoutes.Submission}`
    return this.postEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.CalculationStructure,
    )
  }
  createGseSubmissionStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseSubmissionStructure}/submission`
    return this.postEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.GseSubmissionStructure,
    )
  }
  updateSubmissionStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.creditSubmissionStructure}${CreditApiRoutes.Submission}/${structure._id}`
    return this.putEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.CalculationStructure,
    )
  }
  updateGseSubmissionStructure(
    structure: CreditCalculationStructure,
  ): ApiResponse<CreditCalculationStructure> {
    const endpoint = `${API.gseSubmissionStructure}${CreditApiRoutes.Submission}/${structure._id}`
    return this.putEntity<CreditCalculationStructure>(
      structure,
      endpoint,
      CreditRequests.CalculationStructure,
    )
  }
  getUserIdsBySubmissionId(submissionId: number): ApiResponse<number[]> {
    const endpoint = `${API.creditSubmissionStructure}/${submissionId}${CreditApiRoutes.Users}`
    return this.getEntity<number[]>(endpoint, CreditRequests.UserIdsOfCreditSubmissionStructure)
  }
  getUserIdsByGseSubmissionId(submissionId: number): ApiResponse<number[]> {
    const endpoint = `${API.gseSubmissionStructure}/${submissionId}${CreditApiRoutes.Users}`
    return this.getEntity<number[]>(endpoint, CreditRequests.UserIdsOfCreditSubmissionStructure)
  }
  getGseSubmissionStructureBySubmissionStructureId(
    id: string,
  ): ApiResponse<GseSubmissionStructure> {
    const endpoint = `${API.gseSubmissionStructure}/${id}`
    return this.getEntity<GseSubmissionStructure>(endpoint, CreditRequests.GseSubmissionStructure)
  }
  putGseSubmissionStructure(
    structure: GseSubmissionStructure,
  ): ApiResponse<GseSubmissionStructure> {
    const endpoint = `${API.gseSubmissionStructure}/${structure.id}`
    return this.putEntity<GseSubmissionStructure>(
      structure,
      endpoint,
      CreditRequests.GseSubmissionStructure,
    )
  }
  getGseSubmissionReinsurersBySubmissionId(
    submissionId: number,
  ): ApiResponse<GseSubmissionReinsurer[]> {
    const endpoint = `${API.gseSubmissionReinsurer}`
    const queryParam = this.buildQueryParam(CreditApiParams.SubmissionId, submissionId)
    return this.getEntity<GseSubmissionReinsurer[]>(
      endpoint,
      CreditRequests.GseSubmissionReinsurer,
      queryParam,
    )
  }
  createGseSubmissionReinsurer(
    reinsurer: GseSubmissionReinsurer,
  ): ApiResponse<GseSubmissionReinsurer> {
    return this.postEntity<GseSubmissionReinsurer>(
      reinsurer,
      `${API.gseSubmissionReinsurer}`,
      CreditRequests.GseSubmissionReinsurer,
    )
  }
  updateGseSubmissionReinsurer(
    reinsurer: GseSubmissionReinsurer,
  ): ApiResponse<GseSubmissionReinsurer> {
    return this.putEntity<GseSubmissionReinsurer>(
      reinsurer,
      `${API.gseSubmissionReinsurer}/${reinsurer.id}`,
      CreditRequests.GseSubmissionReinsurer,
    )
  }
  updateGseSubmissionReinsurerStatus(
    reinsurer: GseSubmissionReinsurer,
  ): ApiResponse<GseSubmissionReinsurer> {
    const endpoint = `${API.gseSubmissionReinsurer}${CreditApiRoutes.Status}`
    return this.putEntity<GseSubmissionReinsurer>(
      reinsurer,
      endpoint,
      CreditRequests.GseSubmissionReinsurerStatus,
    )
  }
  getGseSubmissionUserTranchesBySubmissionAndUser(
    submissionId: number,
  ): ApiResponse<GseSubmissionUserTranche[]> {
    const endpoint = `${API.gseSubmissionUserTranche}`
    const queryParam = this.buildQueryParam(CreditApiParams.SubmissionId, submissionId)
    return this.getEntity<GseSubmissionUserTranche[]>(
      endpoint,
      CreditRequests.GseSubmissionUserTranche,
      queryParam,
    )
  }
  saveGseSubmissionUserTranche(
    userTranche: GseSubmissionUserTranche,
  ): ApiResponse<GseSubmissionUserTranche> {
    const endpoint = `${API.gseSubmissionUserTranche}`
    return this.postEntity<GseSubmissionUserTranche>(
      userTranche,
      endpoint,
      CreditRequests.GseSubmissionUserTranche,
    )
  }
  getCreditSubmissionReinsurersBySubmissionId(
    submissionId: number,
  ): ApiResponse<CreditSubmissionReinsurer[]> {
    const endpoint = `${API.creditSubmissionReinsurer}`
    const queryParam = this.buildQueryParam(CreditApiParams.SubmissionId, submissionId)
    return this.getEntity<CreditSubmissionReinsurer[]>(
      endpoint,
      CreditRequests.CreditSubmissionReinsurer,
      queryParam,
    )
  }
  createCreditSubmissionReinsurer(
    submissionReinsurer: CreditSubmissionReinsurer,
  ): ApiResponse<CreditSubmissionReinsurer> {
    const endpoint = `${API.creditSubmissionReinsurer}`
    return this.postEntity<CreditSubmissionReinsurer>(
      submissionReinsurer,
      endpoint,
      CreditRequests.CreditSubmissionReinsurer,
    )
  }
  updateCreditSubmissionReinsurer(
    submissionReinsurer: CreditSubmissionReinsurer,
  ): ApiResponse<CreditSubmissionReinsurer> {
    const endpoint = `${API.creditSubmissionReinsurer}/${submissionReinsurer.id}`
    return this.putEntity<CreditSubmissionReinsurer>(
      submissionReinsurer,
      endpoint,
      CreditRequests.CreditSubmissionReinsurer,
    )
  }
  updateCreditSubmissionReinsurerStatus(
    submissionReinsurer: CreditSubmissionReinsurer,
  ): ApiResponse<CreditSubmissionReinsurer> {
    const endpoint = `${API.creditSubmissionReinsurer}${CreditApiRoutes.Status}`
    return this.putEntity<CreditSubmissionReinsurer>(
      submissionReinsurer,
      endpoint,
      CreditRequests.CreditSubmissionReinsurerStatus,
    )
  }
  getCreditSubmissionUserTranchesBySubmissionAndUser(
    submissionId: number,
  ): ApiResponse<CreditSubmissionUserTranche[]> {
    const endpoint = `${API.creditSubmissionUserTranche}`
    const queryParam = this.buildQueryParam(CreditApiParams.SubmissionId, submissionId)
    return this.getEntity<CreditSubmissionUserTranche[]>(
      endpoint,
      CreditRequests.CreditSubmissionUserTranche,
      queryParam,
    )
  }
  saveCreditSubmissionUserTranche(
    userTranche: CreditSubmissionUserTranche,
  ): ApiResponse<CreditSubmissionUserTranche> {
    const endpoint = `${API.creditSubmissionUserTranche}`
    return this.postEntity<CreditSubmissionUserTranche>(
      userTranche,
      endpoint,
      CreditRequests.CreditSubmissionUserTranche,
    )
  }
  issueQuote(
    structure: CreditSubmissionAuthorizationRequest,
  ): ApiResponse<CreditSubmissionAuthorizationRequest> {
    const endpoint = this.buildQuoteRoute(
      structure.submission_id.toString(),
      structure.tranche_id,
      CreditApiRoutes.Quote,
    )
    return this.postEntity<CreditSubmissionAuthorizationRequest>(
      structure,
      endpoint,
      CreditRequests.CreditQuoteSubmissionAuthorization,
    )
  }
  declineQuote(
    submissionId: string,
    trancheId: string,
    legalEntity: string,
  ): ApiResponse<{ submissionId: string; trancheId: string }> {
    const endpoint = this.buildQuoteRoute(submissionId, trancheId, CreditApiRoutes.Decline)
    return this.postEntityWithResponse<
      { legalEntity: string },
      { submissionId: string; trancheId: string }
    >({ legalEntity }, endpoint, CreditRequests.CreditQuoteDecline)
  }
  getQuoteSummary(submissionId: number): ApiResponse<CreditTrancheQuoteList[]> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.Submission}/${submissionId}${CreditApiRoutes.TrancheSummary}`
    return this.getEntity<CreditTrancheQuoteList[]>(
      endpoint,
      CreditRequests.CreditSubmissionUserTranche,
    )
  }
  getQuoteAuditBySubmissionAndTrancheId(
    submissionId: string,
    trancheId: string,
  ): ApiResponse<CreditQuoteAuditTable> {
    const endpoint = this.buildQuoteRoute(submissionId, trancheId, CreditApiRoutes.Audit)
    return this.getEntity<CreditQuoteAuditTable>(endpoint, CreditRequests.CreditQuoteAuditTable)
  }
  getQuoteAdminOverview(submissionId: string, trancheId: string): ApiResponse<CreditQuoteOverview> {
    const endpoint = this.buildQuoteRoute(submissionId, trancheId, CreditApiRoutes.Overview)
    return this.getEntity<CreditQuoteOverview>(
      endpoint,
      CreditRequests.CreditQuoteReinsurerOverview,
    )
  }
  getQuoteReinsurerOverview(submissionId: number): ApiResponse<CreditQuoteSubmissionOverview> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.Submission}/${submissionId}${CreditApiRoutes.ReinsurerOverview}`
    return this.getEntity<CreditQuoteSubmissionOverview>(
      endpoint,
      CreditRequests.CreditQuoteReinsurerOverview,
    )
  }
  getAllModelScenarioMappings(): ApiResponse<CreditModelScenarioMapping[]> {
    const endpoint = `${API.creditModelScenario}`
    return this.getEntity<CreditModelScenarioMapping[]>(
      endpoint,
      CreditRequests.CreditModelScenarioMapping,
    )
  }
  postIssueFot(data: CreditIssueFot): ApiResponse<CreditIssueFot> {
    const url = this.buildQuoteRoute(
      data.submission_id.toString(),
      data.tranche_id,
      CreditApiRoutes.IssueFot,
    )
    return this.postEntity<CreditIssueFot>(data, url, CreditRequests.CreditIssueFot)
  }
  postIssueAssignedLines(
    submissionId: string,
    trancheId: string,
    authorized: number,
    comment?: string,
    legalEntity?: string,
  ): ApiResponse<Record<string, any>> {
    const url = this.buildQuoteRoute(submissionId, trancheId, CreditApiRoutes.IssueAuthorization)
    return this.postEntity(
      {
        authorized_percent: authorized,
        ...(comment !== undefined && { comments: comment }),
        ...(legalEntity !== undefined && { legal_entity: legalEntity }),
      },
      url,
      CreditRequests.CreditIssueAuthorization,
    )
  }
  issueAuthorization(trancheId: string, authorized: number): ApiResponse<Record<string, any>> {
    const url = `${API.creditQuote}${CreditApiRoutes.Submission}${CreditApiRoutes.IssueAuthorization}/${trancheId}/`
    return this.postEntity(
      {
        authorized_percent: authorized,
      },
      url,
      CreditRequests.CreditIssueAuthorization,
    )
  }
  getAssignedLines(
    submissionId: string,
    trancheId: string,
  ): ApiResponse<CreditQuoteAssignedLineRow[]> {
    const endpoint = this.buildQuoteRoute(submissionId, trancheId, CreditApiRoutes.AssignedLines)
    return this.getEntity<CreditQuoteAssignedLineRow[]>(
      endpoint,
      CreditRequests.CreditQuoteAssignedLines,
    )
  }
  putAssignedLine(line: CreditQuoteAssignedLineRow): ApiResponse<CreditQuoteAssignedLineRow> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.AssignedLines}`
    return this.putEntity<CreditQuoteAssignedLineRow>(
      line,
      endpoint,
      CreditRequests.CreditUpdateAssignedLine,
    )
  }
  getCreditReinsurers(submissionId: string): ApiResponse<Record<number, string>> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.Submission}/${submissionId}${CreditApiRoutes.Reinsurers}`
    return this.getEntity<Record<number, string>>(endpoint, CreditRequests.GetCreditReinsurers)
  }
  postAssignedLine(
    line: CreditQuoteNewAssignedLine,
    trancheId: string,
  ): ApiResponse<CreditQuoteNewAssignedLine> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.AssignedLines}/${trancheId}`
    return this.postEntity<CreditQuoteNewAssignedLine>(
      line,
      endpoint,
      CreditRequests.CreditQuotePostAssignedLine,
    )
  }
  deleteAssignedLine(lineId: number): ApiResponse<number> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.AssignedLines}/${lineId}`
    return this.deleteEntity<number>(endpoint, CreditRequests.CreditQuoteDeleteAssignedLine)
  }
  uploadAssignedLinesDocs(data: Blob): ApiResponse<string> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.Docs}`
    const url = `${environment.internalApi.base}${endpoint}`

    return this.http
      .post<string>(url, data)
      .pipe(
        mapToMaybeData(),
        catchAndHandleError(`POST ${CreditRequests.CreditQuoteUploadAuthDoc}`),
      )
  }
  downloadAssignedLinesDocs(id: string): ApiResponse<string> {
    const endpoint = `${API.creditQuote}${CreditApiRoutes.Docs}/${id}`
    return this.getEntity<string>(endpoint, CreditRequests.CreditQuoteUploadAuthDoc)
  }
  informReinsurersOfSignedPercentage(submissionId: string, trancheId: string): ApiResponse<void> {
    const endpoint =
      this.buildQuoteRoute(submissionId, trancheId, CreditApiRoutes.Notify) +
      CreditApiRoutes.SignedPercent
    return this.getEntity(endpoint, CreditRequests.CreditQuoteInformReinsurersOfSignedPercentage)
  }
  sendCreditEmail(email: CreditEmail): ApiResponse<CreditEmail> {
    const endpoint = `${API.sendEmail}/credit`
    return this.postEntity<CreditEmail>(email, endpoint, CreditRequests.SendEmail)
  }
  /**
   * Simulates LEAPS model run by uploading loan and scenario CSV files
   *
   * This method is used when input files need to be uploaded to blob storage
   * before simulation can begin. The files are uploaded first, then the simulation
   * is initiated using the uploaded file references.
   *
   * @param scenarioFile - Scenario CSV file containing scenario data
   * @param loanFile - Loan CSV file containing loan portfolio data to be simulated
   * @param portfolioId - Unique identifier for the portfolio being simulated
   * @param scenarioMappingId - ID of the scenario mapping that defines model parameters and logic
   * @param nMonths - Number of months to simulate forward (typically 360 for 30-year projections)
   * @param rngSeed - Random seed for reproducible simulation results (e.g., 123456)
   * @param stub - Whether to run in stub mode
   * @param runName - Name of the run
   * @param runDescription - Description of the run
   * @returns Api response containing the simulation response with run ID and status
   */
  simulateUploadCsv(
    scenarioFile: File,
    loanFile: File,
    portfolioId: string,
    scenarioMappingId: number,
    nMonths: number,
    rngSeed: number,
    stub: boolean = false,
    runName: string,
    runDescription: string,
  ): ApiResponse<SimulateLeapsModelResponse> {
    const formData = new FormData()
    formData.append('scenarioFile', scenarioFile)
    formData.append('loanFile', loanFile)
    formData.append('portfolioId', portfolioId)
    formData.append('scenarioMappingId', scenarioMappingId.toString())
    formData.append('NMonths', nMonths.toString())
    formData.append('RngSeed', rngSeed.toString())
    formData.append('Stub', stub.toString())
    formData.append('runName', runName)
    formData.append('runDescription', runDescription)
    const endpoint = `${API.base}${API.creditModel}${CreditApiRoutes.Simulate}${CreditApiRoutes.UploadCsv}`
    return this.http
      .post<SimulateLeapsModelResponse>(endpoint, formData)
      .pipe(mapToMaybeData(), catchAndHandleError(CreditRequests.CreditModelSimulate))
  }
  /**
   * Initiates a credit model simulation using existing blob storage references
   *
   * This method is used when the input files (loan and scenario data) are already stored
   * in blob storage and don't need to be uploaded. The payload contains references
   * to these existing files along with simulation parameters.
   *
   * @param payload - Simulation request containing:
   *   - portfolio_id: ID of the portfolio to simulate
   *   - scenario_mapping_id: ID of the scenario mapping to use
   *   - n_months: Number of months to simulate forward
   *   - rng_seed: Random seed for reproducible simulation results
   *   - stub: Whether to run in stub mode
   *   - loan_parquet_uri: Blob storage URI for loan data file
   *   - scenario_parquet_uri: Blob storage URI for scenario data file
   *   - run_name: Name of the run
   *   - run_description: Description of the run
   * @returns Api response containing the simulation response with run ID and status
   */
  leapsSimulateFromBlob(
    payload: SimulateLeapsRequestPayload,
  ): ApiResponse<SimulateLeapsModelResponse> {
    const endpoint = `${API.creditModel}${CreditApiRoutes.LeapsSimulateFromBlob}`
    return this.postEntityWithResponse<SimulateLeapsRequestPayload, SimulateLeapsModelResponse>(
      payload,
      endpoint,
      CreditRequests.CreditModelSimulate,
    )
  }

  millimanSimulateFromBlob(
    payload: SimulateMillimanRequestPayload,
  ): ApiResponse<SimulateMillimanResults> {
    const endpoint = `${API.creditModel}${CreditApiRoutes.MillimanSimulateFromBlob}`
    return this.postEntityWithResponse<SimulateMillimanRequestPayload, SimulateMillimanResults>(
      payload,
      endpoint,
      CreditRequests.CreditModelMillimanSimulate,
    )
  }

  /**
   * Retrieves statistics for a credit model run
   *
   * @param runId - The ID of the run to get statistics for
   * @returns Api response containing summary, loan gross loss scenario records, and gross loss scenario records
   */
  getStatsForARun(runId: string): ApiResponse<LoanPortfolioSummary> {
    const queryParams = this.buildQueryParam(CreditApiParams.RunId, runId)
    const endpoint = `${API.creditModel}${CreditApiRoutes.LoanStats}`
    return this.getEntity<LoanPortfolioSummary>(
      endpoint,
      CreditRequests.CreditModelStats,
      queryParams,
    )
  }

  /**
   * Retrieves the status of credit model runs with optional filtering and pagination
   *
   * This method fetches a paginated list of model runs, allowing filtering by portfolio
   * and run status. It's used to populate the model runner UI with current and historical
   * run information.
   *
   * @param portfolioId - Optional filter to only return runs for a specific portfolio
   * @param pageNumber - Optional page number for pagination (defaults to 1 if not specified)
   * @param pageSize - Optional page size for pagination (defaults to 50 if not specified)
   * @param statusFilter - Optional filter to only return runs with specific status (queued, running, completed, failed)
   * @returns Observable containing paginated response with:
   *   - jobs: Array of model run logs with metadata
   *   - totalCount: Total number of runs matching the criteria
   *   - pageNumber: Current page number
   *   - pageSize: Number of results per page
   *   - totalPages: Total number of pages
   */
  getAllRunStatus(
    portfolioId?: string,
    pageNumber?: number,
    pageSize?: number,
    statusFilter?: RunStatus,
  ): ApiResponse<ModelRunStatusResponse> {
    const kvps: { key: string; value: string | number | boolean }[] = []
    if (portfolioId) kvps.push({ key: CreditApiParams.PortfolioId, value: portfolioId })
    if (pageNumber) kvps.push({ key: CreditApiParams.PageNumber, value: pageNumber })
    if (pageSize) kvps.push({ key: CreditApiParams.PageSize, value: pageSize })
    if (statusFilter) kvps.push({ key: CreditApiParams.StatusFilter, value: statusFilter })
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.creditModel}${CreditApiRoutes.Runs}`
    return this.getEntity<ModelRunStatusResponse>(
      endpoint,
      CreditRequests.CreditModelRunStatus,
      queryParams,
    )
  }

  getScenarioFileData(
    blobPath: string,
    scenarioMappingId: number,
  ): ApiResponse<CreditGrossLossScenarioDatum[]> {
    const kvps: { key: string; value: string | number | boolean }[] = []
    kvps.push({ key: CreditApiParams.BlobPath, value: blobPath })
    kvps.push({ key: CreditApiParams.ScenarioMappingIdFull, value: scenarioMappingId })
    const queryParams = this.buildQueryParams(kvps)
    const endpoint = `${API.creditModel}${CreditApiRoutes.ScenarioFileData}`
    return this.getEntity<CreditGrossLossScenarioDatum[]>(
      endpoint,
      CreditRequests.CreditModelScenarioFileData,
      queryParams,
    )
  }

  /**
   * Makes a GET request to retrieve an entity from the API
   *
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @param queryParams - Optional query string parameters (without the leading '?')
   * @returns Observable containing the requested entity data
   */
  private getEntity<T>(
    endpoint: string,
    requestType: CreditRequests,
    queryParams?: string,
  ): ApiResponse<T> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http.get<T>(url).pipe(mapToMaybeData(), catchAndHandleError(`GET ${requestType}`))
  }
  /**
   * Makes a GET request with custom data mapping for response transformation
   *
   * Used when the API response needs to be transformed or mapped to a different
   * structure before being returned to the caller.
   *
   * @template T - The target type after mapping
   * @template U - The source type from the API response
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @param mapCustom - Function to transform the API response data to the target type
   * @param queryParams - Optional query string parameters (without the leading '?')
   * @returns Observable containing the mapped entity data
   */
  private getEntityWithMapping<T, U>(
    endpoint: string,
    requestType: CreditRequests,
    mapCustom: (data: U) => T,
    queryParams?: string,
  ): ApiResponse<T> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http.get<U>(url).pipe(
      map((data: U) => mapCustom(data)),
      mapToMaybeData(),
      catchAndHandleError(`GET ${requestType}`),
    )
  }

  /**
   * Makes a GET request to retrieve a blob with response headers
   *
   * Used for file downloads where the response headers contain important
   * metadata like the filename from Content-Disposition header.
   *
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @param queryParams - Optional query string parameters (without the leading '?')
   * @returns Observable containing blob data and extracted filename from headers
   */
  private getBlobWithHeaders(
    endpoint: string,
    requestType: CreditRequests,
    queryParams?: string,
  ): ApiResponse<BlobWithHeaders> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>) => {
        const contentDisposition = response.headers.get(CONTENT_DISPOSITION)
        if (!contentDisposition) {
          console.error(`${CONTENT_DISPOSITION} header is missing.`)
          return { blob: response.body, filename: 'data.csv' }
        }
        const match = contentDisposition.match(/filename="([^"]+)"|filename\*=UTF-8''([^;]+)/)
        const filename = match ? match[1] || match[2] : 'data.csv'
        return { blob: response.body, filename }
      }),
      mapToMaybeData(),
      catchAndHandleError(`GET ${requestType}`),
    )
  }

  /**
   * Makes a POST request to create an entity or submit data to the API
   *
   * @param entity - The data payload to send in the request body
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @param queryParams - Optional query string parameters (without the leading '?')
   * @returns Observable containing the created entity data
   */
  private postEntity<T>(
    entity: T,
    endpoint: string,
    requestType: CreditRequests,
    queryParams?: string,
  ): ApiResponse<T> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http
      .post<T>(url, entity)
      .pipe(mapToMaybeData(), catchAndHandleError(`POST ${requestType}`))
  }

  /**
   * Makes a POST request with a different response type than the entity type
   *
   * Used when the API returns a different structure than what was sent in the request,
   * such as when posting a request object and receiving a response object.
   *
   * @template T - The type of the request body being sent to the API
   * @template U - The type of the response data returned from the API
   * @param entity - The data payload to send in the request body
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @param queryParams - Optional query string parameters (without the leading '?')
   * @returns Observable containing the response data (type U, not T)
   */
  private postEntityWithResponse<T, U>(
    entity: T,
    endpoint: string,
    requestType: CreditRequests,
    queryParams?: string,
  ): ApiResponse<U> {
    const params = queryParams ? `?${queryParams}` : ''
    const url = `${API.base}${endpoint}${params}`
    return this.http
      .post<U>(url, entity)
      .pipe(mapToMaybeData(), catchAndHandleError(`POST ${requestType}`))
  }

  /**
   * Makes a PUT request to update an existing entity in the API
   *
   * @param entity - The updated data payload to send in the request body
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @returns Observable containing the updated entity data
   */
  private putEntity<T>(entity: T, endpoint: string, requestType: CreditRequests): ApiResponse<T> {
    const url = `${API.base}${endpoint}`
    return this.http
      .put<T>(url, entity)
      .pipe(mapToMaybeData(), catchAndHandleError(`PUT ${requestType}`))
  }

  /**
   * Makes a DELETE request to remove an entity from the API
   *
   * @param endpoint - The API endpoint path (relative to base URL)
   * @param requestType - The request type identifier for error tracking and logging
   * @returns Observable containing the deleted entity data (if any)
   */
  private deleteEntity<T>(endpoint: string, requestType: CreditRequests): ApiResponse<T> {
    const url = `${API.base}${endpoint}`
    return this.http
      .delete<T>(url)
      .pipe(mapToMaybeData(), catchAndHandleError(`DELETE ${requestType}`))
  }

  /**
   * Builds a query string from an array of key-value pairs
   *
   * Converts an array of {key, value} objects into a URL-encoded query string
   * with multiple parameters joined by '&'.
   *
   * @param kvps - Array of key-value pairs to convert to query parameters
   * @returns URL-encoded query string (e.g., "key1=value1&key2=value2")
   */
  private buildQueryParams(kvps: { key: string; value: string | number | boolean }[]): string {
    return kvps.map((kvp) => this.buildQueryParam(kvp.key, kvp.value)).join('&')
  }

  /**
   * Builds a single URL-encoded query parameter
   *
   * Converts a single key-value pair into a URL-encoded parameter string.
   * The value is converted to a string and URL-encoded automatically.
   *
   * @param key - The parameter name
   * @param value - The parameter value (string, number, or boolean)
   * @returns URL-encoded parameter string (e.g., "pageNumber=1")
   */
  private buildQueryParam(key: string, value: string | number | boolean): string {
    return `${key}=${value}`
  }

  /**
   * Builds a complete API route for quote-related endpoints
   *
   * Constructs the full endpoint path for quote operations by combining
   * the base quote API route with submission ID, tranche ID, and the specific route.
   * Used for all quote-scoped API calls.
   *
   * @param submissionId - The submission identifier
   * @param trancheId - The tranche identifier within the submission
   * @param route - The specific API route path for the operation
   * @returns Complete endpoint path (e.g., "/api/credit/quote/submission/123/tranche/456/notify")
   */
  private buildQuoteRoute(submissionId: string, trancheId: string, route: CreditApiRoutes): string {
    return `${API.creditQuote}${CreditApiRoutes.Submission}/${submissionId}${CreditApiRoutes.Tranche}/${trancheId}${route}`
  }
}
